import { useState } from "react";
import styles from "./Forms.module.css";
import { Controller, useForm } from "react-hook-form";
import TextInput from "../UILibrary/TextInput";
import { Email } from "Utils/Global/regularExpressions";
import PasswordInput from "../UILibrary/PasswordInput";
import {
  defaultSignUpValues,
  passwordRules,
} from "Utils/Global/globalConstants";
import Submission from "../UILibrary/Submission";
import AlternativeAuth from "../UILibrary/AlternativeAuth";
import { validatePasswordRegExpressions } from "Utils/Global/globalfuncion";
import CheckBox from "../UILibrary/CheckBox";
import PrivacyPolicyModal from "../Modals/PrivacyPolicyModal";
import RadioButton from "../UILibrary/RadioButton";
import { ISignupFormData } from "@model/Authentication.model";
import { AuthenticationService } from "../../../Logic/Authentication/AuthenticationService";
import { useNavigate } from "react-router-dom";
import { UserRegistrationStoreImplementation } from "Data/UserRegistration/UserRegistrationStoreImplementation";
import { isValidIntlPhone } from "Utils/Global/phoneValidation";

const SignupForm: React.FC = () => {
  const { control, handleSubmit, setValue, reset } = useForm<ISignupFormData>({
    mode: "onSubmit",
    defaultValues: {
      ...defaultSignUpValues,
    },
  });

  const [apiError, setApiError] = useState<string | undefined>(undefined);
  const [policyModalVisibility, setPolicyModalVisibility] =
    useState<boolean>(false);
  const [disableCheckBox, setDisableCheckBox] = useState<boolean>(true);
  const auth = new AuthenticationService();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const userRegistrationStore = UserRegistrationStoreImplementation();

  const openPolicyModal = () => setPolicyModalVisibility(true);
  const closePolicyModal = () => {
    setPolicyModalVisibility(false);
    setDisableCheckBox(false);
  };

  const updateCheckBoxState = (agree: boolean) => {
    setValue("agreeToPolicy", agree);
  };

  const onValidSignup = async (form: ISignupFormData) => {
    await auth.handleSignup({
      data: form,
      reset,
      setApiError: (m) => setApiError(m ?? undefined),
      setLoading,
      verifyUser: userRegistrationStore!.verifyUser,
    });
  };

  const handleGoogleLogin = () => {
    console.log("Google Sign Up");
  };

  const handleAppleLogin = () => {
    console.log("Apple Sign Up");
  };

  const navigateToLogin = () => {
    navigate("/auth/login");
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.signupContainer}>
        <div className={styles.signupImage} />

        <div className={styles.signupFormWrapper}>
          <form data-testid="signup_form">
            <h2 className={styles.signupTitle}>Create Account</h2>
            <div className={styles.nameFields}>
              <Controller
                name="firstName"
                control={control}
                rules={{ required: "First name is required" }}
                render={({ field, fieldState }) => (
                  <TextInput
                    label="First Name"
                    placeholder="Enter first name"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                rules={{ required: "Last name is required" }}
                render={({ field, fieldState }) => (
                  <TextInput
                    label="Last Name"
                    placeholder="Enter last name"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>

            <Controller
              name="email"
              control={control}
              rules={{
                required: { value: true, message: "Email is required!" },
                pattern: { value: Email, message: "Invalid email pattern!" },
              }}
              render={({ field, fieldState }) => (
                <TextInput
                  label="Email"
                  placeholder="Enter your email"
                  type="email"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />

            <div className={styles.formRow}>
              <div className={styles.formHalf}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  rules={{
                    required: "Phone is required",
                    validate: (value) =>
                      isValidIntlPhone(value) ||
                      "Invalid phone format. Use +country code.",
                  }}
                  render={({ field, fieldState }) => (
                    <TextInput
                      label="Phone"
                      placeholder="Enter your phone +961 ..."
                      type="tel"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
              <Controller
                name="accountType"
                control={control}
                rules={{ required: "Please select account type" }}
                render={({ field, fieldState }) => (
                  <RadioButton
                    label="Account Type"
                    options={[
                      { label: "Reseller", value: "reseller" },
                      { label: "Client", value: "client" },
                    ]}
                    defaultValue={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    disabled={false}
                  />
                )}
              />
            </div>

            <Controller
              name="password"
              control={control}
              rules={{
                required: "Password is required",
                validate: validatePasswordRegExpressions,
              }}
              render={({ field, fieldState }) => (
                <PasswordInput
                  label="Password"
                  placeholder="Enter your password"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  rules={passwordRules}
                />
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: "Password Confirmation is required",
                validate: (value) =>
                  value === (control as any)._formValues.password ||
                  "Passwords do not match",
              }}
              render={({ field, fieldState }) => (
                <PasswordInput
                  label="Confirm Password"
                  placeholder="Re-enter your password"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  rules={[]}
                />
              )}
            />

            <Controller
              name="agreeToPolicy"
              control={control}
              rules={{ required: "You must agree to the privacy policy" }}
              render={({ field, fieldState }) => (
                <CheckBox
                  value={field.value}
                  onChange={field.onChange}
                  message="Agree to our privacy policy"
                  errorMessage={fieldState.error?.message}
                  messageAction={openPolicyModal}
                  disableCheckBox={disableCheckBox}
                />
              )}
            />

            <Submission
              error={apiError}
              disabled={loading}
              buttonText={loading ? "Signing Up..." : "Sign Up"}
              onClick={handleSubmit(onValidSignup)}
            />

            <div className={styles.redirectLogin}>
              <span className={styles.navigationText}>Already registered?</span>
              <button
                type="button"
                onClick={navigateToLogin}
                className={styles.navigationLink}
              >
                Login
              </button>
            </div>

            <PrivacyPolicyModal
              modalVisibility={policyModalVisibility}
              closeModal={closePolicyModal}
              updateCheckBoxState={updateCheckBoxState}
            />
            {/* <AlternativeAuth
              message="Or Sign up with"
              handleGoogleAction={handleGoogleLogin}
              handleAppleAction={handleAppleLogin}
            /> */}
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignupForm;
